/**
 * RuleSelectionStep Component
 * Step 1 of the tenant deployment dialog - select rules per policy
 */

import React, { useMemo } from 'react';
import {
  Text,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { sortByDeploymentPercent } from '../../common/utils';
import { RuleRow, SelectionListPanel, DialogFooter } from '../shared';
import { convertTenantRuleToPolicyRule } from '../utils';
import '../shared/ruleSelection.css';
import type { RuleSelectionStepProps } from './types';

export const RuleSelectionStep: React.FC<RuleSelectionStepProps> = ({
  deployPolicies,
  activePolicyId,
  policyRuleSelections,
  targetTenantName,
  prefix,
  getDeployedRuleIds,
  onPolicyClick,
  onRuleToggle,
  onToggleRuleExpand,
  expandedRuleIds,
  getPoliciesWithNoRules,
  onNext,
  onClose,
  rightPanelRef,
}) => {
  const policiesWithNoRules = getPoliciesWithNoRules();

  // Transform policies for SelectionListPanel
  const listItems = useMemo(() => {
    const sortedPolicies = sortByDeploymentPercent(
      deployPolicies.map(p => ({
        ...p,
        id: p.policyId,
        deployedCount: getDeployedRuleIds(p.policyId).size,
        totalCount: p.baseRules.length,
      }))
    ).sorted;

    return sortedPolicies.map(policy => {
      const selectedCount = policyRuleSelections[policy.policyId]?.size || 0;
      const deployedRuleIds = getDeployedRuleIds(policy.policyId);
      const totalRules = policy.baseRules.length;
      const deployedCount = policy.baseRules.filter(r => deployedRuleIds.has(r.ruleId)).length;
      const deployPercent = totalRules > 0 ? Math.min((deployedCount / totalRules) * 100, 100) : 0;

      return {
        id: policy.policyId,
        name: policy.policyName,
        subtitle: `${selectedCount} rule${selectedCount !== 1 ? 's' : ''} selected`,
        deployPercent,
        hasError: selectedCount === 0,
      };
    });
  }, [deployPolicies, policyRuleSelections, getDeployedRuleIds]);

  return (
    <>
      {/* Info message */}
      <div className="rsp-info-bar">
        <Text className="rsp-info-text">
          Select rules to deploy from base policies. Rules already deployed to the tenant are marked with a "Deployed" tag.
        </Text>
        {prefix && (
          <Text className="rsp-info-text" style={{ display: 'block', marginTop: 6 }}>
            Parent and standalone labels, as well as policies, will be prefixed with "{prefix}" when first created; existing labels are never renamed, and child labels are not prefixed.
          </Text>
        )}
      </div>

      {/* Main content - Left/Right panels */}
      <div className="rsp-container">
        {/* Left Panel - Policy List */}
        <SelectionListPanel
          headerText={`Selected Policies (${deployPolicies.length})`}
          items={listItems}
          activeId={activePolicyId}
          onItemClick={onPolicyClick}
        />

        {/* Right Panel - Rules for active policy */}
        <div ref={rightPanelRef} className="rsp-right-panel">
          {activePolicyId && (() => {
            const activePolicy = deployPolicies.find(p => p.policyId === activePolicyId);
            if (!activePolicy) return null;

            const baseRules = activePolicy.baseRules;
            const deployedRuleIds = getDeployedRuleIds(activePolicyId);
            const selectedRuleIds = policyRuleSelections[activePolicyId] || new Set<string>();

            return (
              <div style={{ flex: 1 }}>
                <div className="rsp-rules-header">
                  Base Policy Rules ({baseRules.length})
                </div>
                {baseRules.map((rule) => {
                  const isDeployed = deployedRuleIds.has(rule.ruleId);
                  return (
                    <RuleRow
                      key={rule.ruleId}
                      rule={convertTenantRuleToPolicyRule(rule)}
                      isExpanded={expandedRuleIds.has(rule.ruleId)}
                      onToggle={() => onToggleRuleExpand(rule.ruleId)}
                      showCheckbox={true}
                      isChecked={selectedRuleIds.has(rule.ruleId)}
                      onCheckChange={(checked) => onRuleToggle(activePolicyId, rule.ruleId, checked)}
                      showDeploymentTag={true}
                      isDeployed={isDeployed}
                    />
                  );
                })}
                {baseRules.length === 0 && (
                  <div className="rsp-empty-state">
                    No base rules defined for this policy
                  </div>
                )}
              </div>
            );
          })()}

          {!activePolicyId && (
            <div className="rsp-no-selection">
              Select a policy from the left to view and select rules
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <DialogFooter
        onCancel={onClose}
        onPrimary={onNext}
        primaryLabel="Next"
        primaryDisabled={policiesWithNoRules.length > 0}
      >
        {policiesWithNoRules.length > 0 && (
          <MessageBar intent="error" style={{ marginRight: 12 }}>
            <MessageBarBody>
              The following policies have no rules selected: <strong>{policiesWithNoRules.map(p => p.policyName).join(', ')}</strong>.
              Please select at least 1 rule for each policy.
            </MessageBarBody>
          </MessageBar>
        )}
      </DialogFooter>
    </>
  );
};

export default RuleSelectionStep;
